import { AggregationField, GenericAggregations } from "@/services/api/types";
import React, { useMemo } from "react";
import BottomKPIBar, {
  KPI
} from "@/components/controls/BottomKPIBar/BottomKPIBar";
import { find, isEmpty } from "lodash";
import { mergeAggregationGroups } from "@/components/controls/DataTable/groupSummaries";

interface LeadsKPIData extends Record<string, number> {
  total: number;
  avgOpenForDays: number;
}

interface Props {
  aggregations: GenericAggregations<string> | {};
  totalLeads: number;
  isLoading?: boolean;
  isError?: boolean;
  isRowsError?: boolean;
  onOpenStateChange?: (isOpen: boolean) => void;
}

const LeadsBottomKPIBar: React.FC<Props> = ({
  aggregations,
  totalLeads,
  isLoading = false,
  isError = false,
  isRowsError = false,
  onOpenStateChange
}) => {
  // The count comes from the rows request; everything else from the
  // aggregations. Each tile follows its own request's failure.
  const kpis: KPI<LeadsKPIData>[] = [
    { name: "total", isRowsDerived: true },
    { name: "avgOpenForDays" }
  ];

  const getFieldFromData = (
    aggregationsData: AggregationField<string>[],
    fieldName: string,
    operation: "avg" | "sum"
  ) => {
    const agg = find(aggregationsData, (field) => field.name === fieldName);
    return agg?.aggregations?.[operation] || 0;
  };

  const data: LeadsKPIData = useMemo(() => {
    let avgOpenForDays = 0;

    // The response is grouped whenever the table is, so the KPI figures come
    // from every group merged back together rather than from the first one.
    const aggregationsData = mergeAggregationGroups(
      aggregations as GenericAggregations<string>
    )?.fields ?? [];

    if (!isEmpty(aggregationsData)) {
      avgOpenForDays = getFieldFromData(aggregationsData, "openForDays", "avg");
    }

    return {
      total: totalLeads,
      avgOpenForDays
    };
  }, [aggregations, totalLeads]);

  return (
    <BottomKPIBar<LeadsKPIData>
      kpis={kpis}
      data={data}
      pageName="leads"
      isLoading={isLoading}
      isError={isError}
      isRowsError={isRowsError}
      onOpenStateChange={onOpenStateChange}
    />
  );
};

export default LeadsBottomKPIBar;
